import React, { useEffect, useLayoutEffect, lazy, Suspense, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import logo from '../../sidebar-logo.png';
import mixpanel from 'mixpanel-browser';
import {
  VolumeUp as VolumeUpIcon,
  CloudUpload as CloudUploadIcon,
  Language as LanguageIcon,
  Extension as ExtensionIcon,
  PhoneIphone as PhoneIphoneIcon,
  Computer as ComputerIcon,
  Email as EmailIcon,
  PlayArrow as PlayArrowIcon,
  Security as SecurityIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { setLandingPage } from '../../utils/trackingParams';
import { logConversion } from '../../firebase';
import userStore from '../../Stores/User';
import { isPaidProUser, hasPaidPlan } from '../../constants/plans';
import './landingA.scss';

// Lazy load non-critical components
const Testimonials = lazy(() => import('./Components/Testimonials'));
const requestIdleCallback = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));

const ProBadge = observer(() => {
  const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id 
    ? isPaidProUser(userStore.user)
    : false;
  const hasPlan = hasPaidPlan(userStore.user);

  const [showPopover, setShowPopover] = React.useState(false);

  const badgeStyle = {
    background: isProUser 
      ? 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)' 
      : hasPlan 
        ? 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)'
        : 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)',
    padding: '2px 6px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: 'bold',
    position: 'absolute',
    top: isProUser ? '35px' : '0px',
    left: '45px',
    display: 'inline-flex',
    alignItems: 'center',
    height: '16px',
    width: isProUser ? '32px' : hasPlan ? '70px' : '95px',
    justifyContent: 'center',
    cursor: isProUser ? 'default' : 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    color: 'white'
  };

  const popoverStyle = {
    position: 'absolute',
    top: '25px',
    left: '45px',
    background: 'rgba(0, 0, 0, 0.9)',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    width: '200px',
    color: 'white',
    zIndex: 1000,
    pointerEvents: 'none',
    opacity: showPopover ? 1 : 0,
    transition: 'opacity 0.2s ease-in-out',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(5px)'
  };

  if (!userStore.user) return null;

  if (isProUser) {
    return (
      <div style={badgeStyle}>
        PRO
      </div>
    );
  }

  if (hasPlan) {
    return (
      <div style={{ position: 'relative' }}>
        <a 
          href={userStore.customerPortalUrl} 
          target="_blank" 
          rel="noreferrer"
          style={badgeStyle}
          onMouseEnter={() => setShowPopover(true)}
          onMouseLeave={() => setShowPopover(false)}
          onClick={(e) => {
            if (!userStore.customerPortalUrl) {
              e.preventDefault();
            }
          }}
        >
          UPGRADE
        </a>
        <div style={popoverStyle}>
          Upgrade to pro to get premium voices, other languages, and more!
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Link 
        to="/onboarding/ChooseVoice" 
        style={badgeStyle}
        onMouseEnter={() => setShowPopover(true)}
        onMouseLeave={() => setShowPopover(false)}
      >
        START TRIAL
      </Link>
      <div style={popoverStyle}>
        Start your 7-day free trial today!
      </div>
    </div>
  );
});

const LandingFeatures = observer(() => {
  const cta = "Start listening";
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  useLayoutEffect(() => {
    // Scroll to top immediately before paint
    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    requestIdleCallback(() => {
      mixpanel.track('Features Landing Page Visited', {
        page: 'landing_features'
      });
      setLandingPage('landing_features');
    });
  }, []);

  const renderSignUpButton = useCallback(() => {
    const handleSignUpClick = () => {
      setTimeout(() => {
        mixpanel.track('Click Sign Up (Nav)', {
          placement: 'Features',
          variant: 'A'
        });
        logConversion('sign_up_click', 'A');
      }, 0);
    };

    return (
      <div className="cta-div" style={{ position: 'static', width: 'auto' }}>
        <Link
          to="/signin?source=nav_signup&redirected_from=features"
          className="cta-button"
          onClick={handleSignUpClick}
        >
          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <div>
              <div>{cta}</div>
            </div>
          </div>
        </Link>
      </div>
    );
  }, [cta]);

  const renderCta = useCallback(() => {
    const redirectUrl = "/signin?source=features&redirected_from=features";
    
    const handleCtaClick = () => {
      setTimeout(() => {
        mixpanel.track('Click Try For Free', {
          placement: 'Features',
          variant: 'A'
        });
        logConversion('try_for_free_click', 'A');
      }, 0);
    };
    
    return (
      <div className="cta-div" style={{ position: 'static' }}>
        <Link
          to={redirectUrl}
          className="cta-button"
          onClick={handleCtaClick}
        >
          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <div>
              <div>Start Listening Today</div>
            </div>
          </div>
        </Link>
      </div>
    );
  }, []);

  return (
    <div className="App" style={{ contain: 'layout style paint' }}>
      <Helmet>
        <title>Powerful Text-to-Speech Features You'll Love | Audioread</title>
        <meta name="description" content="Explore natural AI voices, multilingual support, and seamless text-to-audio conversion. Experience features that sound as smart as they feel." />
        <meta property="og:title" content="Powerful Text-to-Speech Features You'll Love | Audioread" />
        <meta property="og:description" content="Explore natural AI voices, multilingual support, and seamless text-to-audio conversion. Experience features that sound as smart as they feel." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="https://audioread.com/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Powerful Text-to-Speech Features You'll Love | Audioread" />
        <meta name="twitter:description" content="Explore natural AI voices, multilingual support, and seamless text-to-audio conversion. Experience features that sound as smart as they feel." />
        <meta name="twitter:image" content="https://audioread.com/logo.png" />
      </Helmet>
      <link rel="preload" href={logo} as="image" />
      
      <header
        className="App-header"
        style={{
          minHeight: '100vh',
        }}
      >
        <div className="nav-bar">
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <div style={{ position: "relative" }} className="logo-container">
              <Link to="/">
                <img 
                  className="audiblogs-logo" 
                  src={logo} 
                  alt="logo" 
                  loading="eager"
                  decoding="sync"
                  style={{ contain: 'layout style paint' }}
                />
              </Link>
              <ProBadge />
            </div>
          </div>

          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '20px', fontSize: '1.05rem', fontWeight: 'bold' }}>
              <Link
                className="signin-button"
                to="/signin?source=nav_signin&redirected_from=features"
              >
                Sign In
              </Link>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', marginLeft: '-20px'}}>
              {renderSignUpButton()}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  marginLeft: '5px',
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  flexShrink: 0
                }}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
              </button>
            </div>
          </div>

          <div className="mobile-nav">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', paddingBottom: '15px', marginRight: '-10px'}}>
              {renderSignUpButton()}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  marginLeft: '5px',
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  flexShrink: 0
                }}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Navigation Menu (shared for desktop and mobile) - Outside nav-bar to prevent clipping */}
        {isMenuOpen && (
          <div 
            style={{
              position: 'fixed',
              top: '80px',
              right: '20px',
              backgroundColor: 'rgba(9, 2, 19, 0.95)',
              padding: '20px',
              width: '220px',
              borderRadius: '10px',
              backdropFilter: 'blur(10px)',
              zIndex: 10000,
              boxSizing: 'border-box',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'visible'
            }}
          >
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link
                to="/features"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Features from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Features
              </Link>
              <Link
                to="/how-it-works"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click How It Works from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                How It Works
              </Link>
              <Link
                to="/integrations-and-platforms"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Integrations from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Integrations
              </Link>
              <Link
                to="/solutions-and-accessibility"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Solutions from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Solutions
              </Link>
              <Link
                to="/organizations"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Organizations from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Organizations
              </Link>
              <Link
                to="/api"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click API from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                API
              </Link>
            </div>
          </div>
        )}

        <h1 className="bigger-headline" style={{ 
          marginTop: '60px',
          paddingTop: '0px',
        }}>
          Explore Audioread's Powerful Features
        </h1>

        <h2 className="subheadline">
          Smarter text-to-speech features designed to keep you productive, informed, and hands-free, whether you're reading a research paper, catching up on newsletters, or converting a long article to listen on the go.
        </h2>
      </header>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <VolumeUpIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Ultra-Realistic AI Voices</h1>
        </div>
        <p className="details">
          Experience AI voices so lifelike they sound indistinguishable from professional narrators. Perfectly suited for long-form listening, these voices are optimised for clarity, pace, and expression, making even the driest material easy to follow.
        </p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Choose from a wide range of voice styles and accents.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Ideal for articles, academic content, technical documentation, and more.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Built for minimal fatigue during extended listening sessions.
          </li>
        </ul>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <CloudUploadIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Convert Articles, PDFs, Emails & More to Audio</h1>
        </div>
        <p className="details">
          With just a few clicks, convert articles to audio and listen whenever it suits you.
        </p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Paste a URL, upload a file, or forward an email. Audioread takes care of the rest.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Supports formats including web pages, PDFs, plain text, emails, and RSS feeds.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Convert instantly from your browser, mobile device, or desktop.
          </li>
        </ul>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <LanguageIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Multilingual Text-to-Speech</h1>
        </div>
        <p className="details">
          Audioread supports multilingual text-to-speech in over 80 languages, ideal for language learners, international professionals, and global content consumption.
        </p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Select from region-specific voice options for authentic pronunciation.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Listen in your native language or practice another.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Switch languages as needed without changing tools.
          </li>
        </ul>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <ExtensionIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Built to Fit Your Workflow</h1>
        </div>
        <p className="details">
          Audioread adapts to how you work and browse:
        </p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ExtensionIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div><strong style={{ background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Browser Extensions:</strong> Highlight and convert directly from Chrome, Edge, or Brave.</div>
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PhoneIphoneIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div><strong style={{ background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>iOS Shortcuts:</strong> Use Safari's Share Sheet to convert web content to audio.</div>
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ComputerIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div><strong style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Android PWA:</strong> Convert from any browser with a tap.</div>
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmailIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div><strong style={{ background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Email Forwarding & Drag/Drop:</strong> Send content to Audioread without breaking flow.</div>
          </li>
        </ul>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <PlayArrowIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Try It in Your Podcast App</h1>
        </div>
        <p className="details">
          Once converted, your content is delivered via a private podcast RSS feed. Subscribe once and stream all future audio content in:
        </p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Apple Podcasts
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Spotify
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Pocket Casts
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Overcast
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Google Podcasts
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            <Link to="/integrations-and-platforms" style={{ color: '#4292fa', textDecoration: 'underline' }}>
              ...and more
            </Link>
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px' }}>
          Or, use the in-browser player for instant playback without installations.
        </p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <SecurityIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4caf50 0%, #2196F3 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4caf50 0%, #2196F3 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Privacy First</h1>
        </div>
        <p className="details">
          Audioread never shares your content. With <strong>Privacy Mode</strong>, your uploads are auto-deleted after 30 days. No indexing, no exposure, and no clutter.
        </p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <h1 className="headline" style={{ marginBottom: '30px' }}>What Our Users Say</h1>
        <Suspense fallback={<div style={{ textAlign: 'center', padding: '40px' }}>Loading testimonials...</div>}>
          <Testimonials />
        </Suspense>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <h1 className="headline">Ready to Listen Smarter?</h1>
        <p className="details">
          Explore how Audioread's <strong>text-to-speech features</strong> help you convert, stream, and learn on your terms.
        </p>
        <div style={{ marginTop: '30px' }}>
          {renderCta()}
        </div>
      </div>

      <div className="footer-links" style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/dmca">DMCA Policy</Link>
        <a href="https://audioread.tolt.io/" target="_blank" rel="noopener noreferrer">Affiliate Program</a>
      </div>
      <div style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          
      </div>
      <div style={{ margin: '25px', opacity: 0.7 }}>© {new Date().getFullYear()} Audioread™. All rights reserved. | <a href="mailto:info@audioread.com" target="_blank" rel="noopener noreferrer">
          Contact us at info@audioread.com
        </a></div>
    </div>
  );
});

export default LandingFeatures;

